import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SubmissionStatus } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { adminApi } from './api';

const STATUS_TONE: Record<SubmissionStatus, string> = {
  new: 'bg-brand/15 text-brand-soft',
  reviewing: 'bg-warning/15 text-warning',
  accepted: 'bg-success/15 text-success',
  declined: 'bg-surface-raised text-ink-faint',
};

function ForumsToggle() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-settings'], queryFn: adminApi.getSettings });
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (data?.devPortalMessage != null) setMessage(data.devPortalMessage);
  }, [data?.devPortalMessage]);

  const save = useMutation({
    mutationFn: (open: boolean) => adminApi.setDevPortal(open, message),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-settings'] }),
  });

  const open = data?.devPortalOpen ?? true;

  return (
    <div className="card mb-6 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-bold text-ink">Dev Forums</h3>
          <p className="text-sm text-ink-muted">
            {open ? 'Open — fans can submit pitches and upload requests.' : 'Closed — submissions are paused.'}
          </p>
        </div>
        <Button
          variant={open ? 'secondary' : 'primary'}
          size="md"
          onClick={() => save.mutate(!open)}
          disabled={save.isPending}
        >
          {open ? 'Close forums' : 'Open forums'}
        </Button>
      </div>
      <div className="mt-3">
        <label htmlFor="closed-msg" className="mb-1 block text-xs text-ink-faint">
          Closed-state message (shown when closed)
        </label>
        <div className="flex gap-2">
          <input
            id="closed-msg"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={280}
            placeholder="Submissions are paused right now — check back soon."
            className="h-10 flex-1 rounded-xl border border-border bg-surface px-3 text-sm text-ink placeholder:text-ink-faint focus:border-brand"
          />
          <Button variant="secondary" size="md" onClick={() => save.mutate(open)} disabled={save.isPending}>
            Save message
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Submissions() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-submissions'], queryFn: () => adminApi.submissions() });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SubmissionStatus }) =>
      adminApi.setSubmissionStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-submissions'] }),
  });

  const subs = data?.submissions ?? [];

  return (
    <div>
      <ForumsToggle />
      <h3 className="mb-3 font-display text-base font-bold text-ink">Submissions</h3>
      {isLoading ? (
        <p className="text-sm text-ink-faint">Loading…</p>
      ) : subs.length === 0 ? (
        <p className="text-sm text-ink-muted">No submissions yet.</p>
      ) : (
        <ul className="space-y-3">
          {subs.map((s) => (
            <li key={s.id} className="card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-surface-raised px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-muted">
                  {s.type === 'show_idea' ? 'Show pitch' : 'Upload request'}
                </span>
                <span className={cn('rounded-full px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide', STATUS_TONE[s.status])}>
                  {s.status}
                </span>
                <span className="ml-auto text-xs text-ink-faint">
                  {new Date(s.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="mt-2 font-medium text-ink">{s.title}</p>
              <p className="text-xs text-ink-faint">
                {s.name} · {s.email}
              </p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-ink-muted">{s.details}</p>
              {s.links && (
                <p className="mt-2 break-all text-xs text-brand-soft">{s.links}</p>
              )}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-ink-faint">Status</span>
                <select
                  value={s.status}
                  onChange={(e) => setStatus.mutate({ id: s.id, status: e.target.value as SubmissionStatus })}
                  className="h-8 rounded-lg border border-border bg-surface-raised px-2 text-sm text-ink"
                >
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="accepted">Accepted</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
